import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from './AnalyticsManager';

const LOOP_WINDOW_MS = 5 * 60 * 1000;

const LOOP_THRESHOLDS = {
  cartIdChanges: 3,
  checkoutMounts: 5,
  paymentIntentRequests: 10,
} as const;

export interface CheckoutCartSnapshot {
  cartId: string;
  isLastChangeRestored: boolean;
  paymentIntentRevision: number;
}

export interface PaymentIntentFailure {
  errorType?: string;
  status?: number;
}

/**
 * Per-page-load checkout counters, sent as first-party diagnostic events so a
 * checkout loop or an unexpected empty cart in production can be explained.
 * Never sent to a brand's own analytics providers.
 */
class CheckoutDiagnostics {
  private cartIdChanges = 0;

  private cartSnapshot: CheckoutCartSnapshot | null = null;

  private checkoutMounts = 0;

  private hasReportedLoop = false;

  private lastPaymentIntentFailure: PaymentIntentFailure | null = null;

  private paymentIntentFailures = 0;

  private paymentIntentRequests = 0;

  private recentPaymentIntentRequestTimes: number[] = [];

  private storageCartEvents = 0;

  recordCartIdChange() {
    this.cartIdChanges += 1;
    this.checkForLoop();
  }

  recordCheckoutMount() {
    this.checkoutMounts += 1;
    this.checkForLoop();
  }

  recordPaymentIntentFailure(failure: PaymentIntentFailure) {
    this.paymentIntentFailures += 1;
    this.lastPaymentIntentFailure = failure;
  }

  recordPaymentIntentRequest(cartSnapshot: CheckoutCartSnapshot) {
    const now = Date.now();
    this.cartSnapshot = cartSnapshot;
    this.paymentIntentRequests += 1;
    this.recentPaymentIntentRequestTimes = [
      ...this.recentPaymentIntentRequestTimes.filter(
        (time) => time > now - LOOP_WINDOW_MS,
      ),
      now,
    ];
    this.checkForLoop();
  }

  /** A write to the saved cart arrived from another tab. */
  recordStorageCartEvent() {
    this.storageCartEvents += 1;
  }

  reportEmptyCart(details: Record<string, unknown>) {
    this.send(ANALYTICS_EVENT.CHECKOUT_EMPTY_CART_SHOWN, details);
  }

  private checkForLoop() {
    if (this.hasReportedLoop) return;

    const thresholdsExceeded = [
      this.recentPaymentIntentRequestTimes.length >=
      LOOP_THRESHOLDS.paymentIntentRequests
        ? 'paymentIntentRequests'
        : null,
      this.cartIdChanges >= LOOP_THRESHOLDS.cartIdChanges
        ? 'cartIdChanges'
        : null,
      this.checkoutMounts >= LOOP_THRESHOLDS.checkoutMounts
        ? 'checkoutMounts'
        : null,
    ].filter(Boolean);

    if (thresholdsExceeded.length === 0) return;

    this.hasReportedLoop = true;
    this.send(ANALYTICS_EVENT.CHECKOUT_LOOP_DETECTED, { thresholdsExceeded });
  }

  private send(eventName: ANALYTICS_EVENT, details: Record<string, unknown>) {
    analyticsManager.sendEvent(
      eventName,
      {
        [ANALYTICS_PROPERTY.Diagnostics]: JSON.stringify({
          cart: this.cartSnapshot,
          counts: {
            cartIdChanges: this.cartIdChanges,
            checkoutMounts: this.checkoutMounts,
            paymentIntentFailures: this.paymentIntentFailures,
            paymentIntentRequests: this.paymentIntentRequests,
            paymentIntentRequestsLast5Min:
              this.recentPaymentIntentRequestTimes.length,
            storageCartEvents: this.storageCartEvents,
          },
          isDocumentVisible: document.visibilityState !== 'hidden',
          lastPaymentIntentFailure: this.lastPaymentIntentFailure,
          secondsSincePageLoad: Math.round(performance.now() / 1000),
          ...details,
        }),
      },
      { providers: [] },
    );
  }
}

export const checkoutDiagnostics = new CheckoutDiagnostics();
